import { PlatformLocation } from '@angular/common';
import { Component, Inject, Optional } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import * as moment from 'moment';
import { Observable, concat } from 'rxjs';
import { finalize, map, tap } from 'rxjs/operators';
import { Country } from '../../../../Foundation/models/enums/country.enum';
import { IJurisdiction, Jurisdiction } from '../../../../foundation/models/enums/jurisdiction.enum';
import { BaseComponent } from '../../../../shared/base-components/base.component';
import { DriverAttributeTypeLookups } from '../../../models/credential/driver-attribute-type-lookups.model';
import { DriverCredentialTypeAttributeTypeMap } from '../../../models/credential/driver-credential-type-attribute-type-map.model';
import { DriverOOJCredentialRequest } from '../../../models/credential/driver-ooj-credential-request.model';
import { DriverOOJCredential } from '../../../models/credential/driver-ooj-credential.model';
import { DriverAttributeType } from '../../../models/enums/driver-attribute-type.enum';
import { AttributeTypeService } from '../../../services/attribute-type.service';
import { CredentialIssuanceApplicationService } from '../../../services/credential-issuance-application.service';
import { CredentialTypeAttributeTypeMapService } from '../../../services/credential-type-attribute-type-map.service';
import { DriverCredentialService } from '../../../services/driver-credential.service';
import { LicenseClassTypeService } from '../../../services/license-class-type.service';

/**
 * Serves as the context for the customer search view modal dialog
 */
export class OOJCredentialViewContext {
    constructor(
        public businessTransactionId: number,
        public id: number,
        public isForTransaction: boolean = true,
        public customerNumberId: number = null
    ) {
    }
}

@Component({
    selector: 'ooj-credential-modal',
    templateUrl: './ooj-credential-modal.html'
})
export class OOJCredentialViewModalComponent extends BaseComponent {
    public form: UntypedFormGroup;
    public countries: any[] = [];
    public licenseClassTypes: any[] = [];
    public endorsementTypes: any[] = [];
    public restrictionTypes: any[] = [];
    public id: number;

    private transactionNumber = 0;
    private oojCredential: DriverOOJCredential = new DriverOOJCredential();
    private jurisdictions: any[] = [];
    private states: any[] = [];
    private credentialTypeAttributeTypeMaps: DriverCredentialTypeAttributeTypeMap[] = new Array();
    private driverAttributeTypeLookups: DriverAttributeTypeLookups;
    // Constants
    private stateNonUsCode = 'Other';
    private stateNonUsId = 0;
    private usCode: number = new Jurisdiction().AZ.country;
    private azId: number = new Jurisdiction().AZ.id;
    private learnersPermitId: number = new DriverAttributeType().InstructionPermit.id;
    public isLoading = false;

    /**
     * Constructs the modal dialog
     * @param public dialog The dialog reference and dialog data
     */
    constructor(
        private driverCredentialService: DriverCredentialService,
        public dialogRef: MatDialogRef<OOJCredentialViewModalComponent>,
        @Optional() @Inject(MAT_DIALOG_DATA) public data: OOJCredentialViewContext,
        private licenseClassTypeService: LicenseClassTypeService,
        private attributeTypeService: AttributeTypeService,
        private credentialTypeAttributeTypeMapService: CredentialTypeAttributeTypeMapService,
        private credentialIssuanceApplicationService: CredentialIssuanceApplicationService,
        formBuilder: UntypedFormBuilder,
        location: PlatformLocation
    ) {
        super();
        this.displayResults = false;
        this.transactionNumber = this.data.businessTransactionId;
        this.id = this.data.id;
        this.form = formBuilder.group({
            id: [0],
            credentialNumber: ['', Validators.required],
            travelId: [false],
            issueDate: [null, Validators.required],
            expirationDate: [null, Validators.required],
            stateId: [null],
            countryId: [this.usCode, Validators.required],
            licenseClassTypeIds: [[], Validators.required],
            restrictionTypeIds: [[]],
            endorsementTypeIds: [[]],
            active: [true],
        },
        {
            validators: Validators.compose([this.stateIdValidator, this.dateValidator])
        });

        location.onPopState(() => {
            this.dialogRef.close();
        });
    }

    private stateIdValidator(group: UntypedFormGroup) {
        const stateId = group.get('stateId');
        const countryId = group.get('countryId');
        let countryEnum = new Country();

        // Must select a state if the country is US
        if (!!countryId && countryId.value == countryEnum.US.id && (!stateId || !stateId.value)) {
            return { 'stateIdRequired': true };
        }
    }

    private dateValidator(group: UntypedFormGroup) {
        const issueDate = group.get('issueDate');
        const expirationDate = group.get('expirationDate');

        if (!!issueDate.value && !!expirationDate.value && moment(expirationDate.value) < moment(issueDate.value)) {
            return { 'issueDateMustBeBeforeExpirationDate': true };
        }
    }

    public ngOnInit(): void {
        concat(
            this.initPage(),
            this.getOojCredential(this.id))
            .subscribe(_ => {
                if ((this.id == null) || (this.id == 0)) {
                    this.form.get('countryId').setValue(this.usCode);
                }

                this.displayResults = true;
            });
    }

    public countryChange = (): void => {
        let formControl = this.form.get('stateId');
        formControl.setValue(null);
        formControl.markAsPristine();
        formControl.markAsUntouched();
    };

    /**
     * Initializes the page
     */
    private initPage = (): Observable<any> => {
        this.countries = new Country().GetActive();
        this.jurisdictions = new Jurisdiction().GetActive();
        this.states = this.jurisdictions.filter(e => e.id != this.azId);
        this.states.push({ id: this.stateNonUsId, code: this.stateNonUsCode });
        return concat(
            this.getLicenseClasses()
                .pipe(tap(r => this.licenseClassTypes = r)),
            this.setDriverCredentialTypeEndorsementTypeMaps()
                .pipe(tap(r => this.credentialTypeAttributeTypeMaps = r)),
            this.attributeTypeService.getAttributeTypeLookup()
                .pipe(tap(r => this.driverAttributeTypeLookups = r)));
    };

    public getJurisdictions = (): IJurisdiction[] => this.states.filter(e => e.country == this.form.get('countryId').value);

    /**
     * Gets the OOJ Credential being edited, if that is relevant
     */
    private getOojCredential = (id: number): Observable<any> => {
        if (0 == +id) {
            return Observable.create(o => {
                o.next(null);
                o.complete();
            });
        }

        return this.driverCredentialService.getOojCredentialForBusinessTransaction(this.transactionNumber, id)
            .pipe(map(
                r => this.populateFromResponse(r),
                e => this.handleValidationResult(e)
            ));
    };

    private populateFromResponse = (response: any): void => {
        if (response != null) {
            this.form.get('id').setValue(response.id);
            this.form.get('credentialNumber').setValue(response.credentialNumber);
            this.form.get('travelId').setValue(response.travelId);
            this.form.get('stateId').setValue(response.stateId);
            this.form.get('countryId').setValue(response.countryId);
            this.form.get('licenseClassTypeIds').setValue((response.licenseClassTypeIds || []).filter((v, i, a) => i == a.indexOf(v)));
            this.form.get('restrictionTypeIds').setValue((response.restrictionTypeIds || []).filter((v, i, a) => i == a.indexOf(v)));
            this.form.get('endorsementTypeIds').setValue((response.endorsementTypeIds || []).filter((v, i, a) => i == a.indexOf(v)));
            this.form.get('active').setValue(response.active);

            let issueDate = null;
            let issueDateMoment = moment(response.issueDate);
            let expirationDate = null;
            let expirationDateMoment = moment(response.expirationDate);

            if (issueDateMoment.isValid()) {
                issueDate = issueDateMoment.format('MM/DD/YYYY');
            }

            if (expirationDateMoment.isValid()) {
                expirationDate = expirationDateMoment.format('MM/DD/YYYY');
            }

            this.form.get('issueDate').setValue(issueDate);
            this.form.get('expirationDate').setValue(expirationDate);
            this.licenseClassTypeSelectionChanged();
        }
    };

    /**
     * Sets the driver credential type endorsement type maps
     */
    private setDriverCredentialTypeEndorsementTypeMaps = (): Observable<any[]> => this.credentialTypeAttributeTypeMapService.getCredentialTypeAttributeMaps()
        .pipe(map(r => {
            if (null == r) {
                return [];
            }

            return r.driverCredentialTypeAttributeTypeMaps;
        },
        e => this.handleValidationResult(e)));

    /**
     * Sets the license classes
     */
    private getLicenseClasses = (): Observable<any[]> => this.licenseClassTypeService.getLicenseClasses()
        .pipe(map(r => {
            let licenseClassTypes: any[] = [];

            for (var i = 0; i < r.driverLicenseClassTypes.length; i++) {
                licenseClassTypes.push({
                    label: r.driverLicenseClassTypes[i].driverLicenseClassTypeDisplayValue,
                    value: +r.driverLicenseClassTypes[i].id
                });
            }

            return licenseClassTypes;
        },
        e => this.handleValidationResult(e)));

    /**
     * Resets endorsements and restrictions based on new license class selections
     */
    public licenseClassTypeSelectionChanged = (): void => {
        this.endorsementTypes = this.getEndorsementTypes();
        this.restrictionTypes = this.getRestrictionTypes();
        this.reconcileSelectedAttributes();
    };

    private getEndorsementTypes = (): any[] => {
        let selectedCredentialTypes = this.form.get('licenseClassTypeIds').value;
        let selectedCredentialTypeMaps = this.credentialTypeAttributeTypeMaps.filter(e => selectedCredentialTypes.some(i => i == e.driverLicenseClassTypeId));
        return this.driverAttributeTypeLookups.lookups.endorsementTypes.filter(e => selectedCredentialTypeMaps.some(i => i.driverAttributeTypeId == e.value))
            .map(e => <any> {
                label: e.text,
                value: +e.value
            });
    };

    private getRestrictionTypes = (): any[] => {
        let selectedCredentialTypes = this.form.get('licenseClassTypeIds').value;
        let selectedCredentialTypeMaps = this.credentialTypeAttributeTypeMaps.filter(e => selectedCredentialTypes.some(i => i == e.driverLicenseClassTypeId));
        return this.driverAttributeTypeLookups.lookups.restrictionTypes.filter(e => selectedCredentialTypeMaps.some(i => i.driverAttributeTypeId == e.value))
            .map(e => <any> {
                label: e.text,
                value: +e.value
            });
    };

    /**
     * Unselectes things that are no longer selectable
     */
    private reconcileSelectedAttributes = (): void => {
        let selectedEndorsementTypes = this.form.get('endorsementTypeIds').value;
        let validEndorsementTypes = this.getEndorsementTypes();
        let invalidEndorsementSelections = selectedEndorsementTypes.filter(e => false == validEndorsementTypes.some(e2 => e == e2.value));

        // check to see if some selected endorsement type ids do not exist
        if (0 != invalidEndorsementSelections.length) {
            invalidEndorsementSelections.forEach(e => selectedEndorsementTypes.remove(e));
            this.form.get('endorsementTypeIds').setValue(selectedEndorsementTypes);
        }

        let selectedRestrictionTypes = this.form.get('restrictionTypeIds').value;
        let validRestrictionTypes = this.getRestrictionTypes();
        let invalidRestrictionSelections = selectedRestrictionTypes.filter(e => false == validRestrictionTypes.some(e2 => e == e2.value));

        // check to see if some selected endorsement type ids do not exist
        if (0 != invalidRestrictionSelections.length) {
            invalidRestrictionSelections.forEach(e => selectedRestrictionTypes.remove(e));
            this.form.get('restrictionTypeIds').setValue(selectedRestrictionTypes);
        }
    };

    /**
     * Closes the modal dialog with no return result
     */
    public cancel = (): void => {
        this.dialogRef.close();
    };

    /**
     * Saves the OOJ credential to WIP
     */
    public save = (): void => {
        if (true != this.form.valid) {
            return;
        }

        this.isLoading = true;
        const credential = new DriverOOJCredential();
        let saveCredential: Observable<any>;

        credential.id = this.form.get('id').value;
        credential.credentialNumber = this.form.get('credentialNumber').value;
        credential.travelId = this.form.get('travelId').value;
        credential.stateId = this.form.get('stateId').value;
        credential.countryId = this.form.get('countryId').value;
        credential.licenseClassTypeIds = this.form.get('licenseClassTypeIds').value;
        credential.restrictionTypeIds = this.form.get('restrictionTypeIds').value;
        credential.endorsementTypeIds = this.form.get('endorsementTypeIds').value;
        credential.issueDate = this.form.get('issueDate').value;
        credential.expirationDate = this.form.get('expirationDate').value;
        credential.active = this.form.get('active').value;

        if (this.data.isForTransaction) {
            const request = new DriverOOJCredentialRequest();
            request.businessTransactionId = this.transactionNumber;
            request.driverOOJCredential = credential;
            saveCredential = this.credentialIssuanceApplicationService.saveOOJCredential(request);
        } else {
            saveCredential = this.credentialIssuanceApplicationService.saveOOJCredentialNoTransaction(credential, this.data.customerNumberId);
        }

        saveCredential.pipe(finalize(() => this.isLoading = false)).subscribe(
            () => this.dialogRef.close(true),
            e => this.handleValidationResult(e)
        );
    };
}
